import { IDecideNextStep } from "projects/borrower-app/src/app/services/wizard/next-step-decider/next-step-decider.interface";
import { WizardFlowContext } from "../../../wizard-flow-context.model";
import { WizardStepBase } from "../../wizard-step-base.model";
import { FieldConfigUtils } from "../field-config-utils";
import { GenericStepConfig } from "../../generic-step-config.model";
import { StepConfig } from "../step-config.model";
import { NextStepDeciderFactory } from "./next-step-decider.factory";
import { StepFactoryBase } from "./step-factory-base";
import { ICreateStep } from "./step-factory.interface";

export type Constructor<T> = new (...args: any[]) => T;

export class GenericStepFactory<TStep extends WizardStepBase> extends StepFactoryBase<GenericStepConfig> implements ICreateStep {

  private readonly _nextStepDeciderFactory: NextStepDeciderFactory;
  private readonly _cls: Constructor<TStep>;

  constructor(nextStepDeciderFactory: NextStepDeciderFactory, cls: Constructor<TStep>) {
    super();
    this._nextStepDeciderFactory = nextStepDeciderFactory;
    this._cls = cls;
  }

  configure(step: WizardStepBase, config: StepConfig): void {
    config.explanation = step.explanation;
    config.title = step.title;
    config.heading = step.heading;
    config.groupId = step.groupId;
    config._i18n = step._i18n;
  }

  create(config: StepConfig, wizardFlowContext: WizardFlowContext): TStep {
    let nextStepDecider: IDecideNextStep | undefined = undefined;
    if (config.nextStepDeciderConfig) {
      nextStepDecider = this._nextStepDeciderFactory.create(config.nextStepDeciderConfig, wizardFlowContext);
    }
    const step: TStep = this.createStepInstance(config.id, config.name, config.path, nextStepDecider, this._cls);
    step.title = config.title;
    step.explanation = config.explanation;
    step.heading = config.heading;
    step.groupId = config.groupId;
    // Phase 1 of `_i18n` config-ownership: make `config._i18n` the single canonical
    // translations bag and alias the runtime step to that SAME object. Edits made via
    // either node then mutate one bag, so the `configure()` back-sync (config._i18n =
    // step._i18n) is a harmless no-op and config-written translations (e.g. converged
    // action labels) survive save. NOTE: LocalizedEditService.set must never replace or
    // delete this object — only its lang/field keys — or the shared identity breaks.
    if (!config._i18n) {
      config._i18n = {};
    }
    step._i18n = config._i18n;
    // Always generate the default field config
    const defaultFieldConfig = FieldConfigUtils.generateDefaultFieldConfigForStep(config.stepType);
    const fieldConfig = config.fieldConfig && Object.keys(config.fieldConfig).length ? { ...config.fieldConfig } : {};

    // Merge missing fields from the default into the loaded config
    for (const key of Object.keys(defaultFieldConfig)) {
      if (!(key in fieldConfig)) {
        fieldConfig[key] = defaultFieldConfig[key];
      }
    }
    // Remove fields that are not present in the default config (admin field removal)
    for (const key of Object.keys(fieldConfig)) {
      if (!(key in defaultFieldConfig)) {
        delete fieldConfig[key];
      }
    }
    // Optionally, you could do a deep merge if fieldConfig values are nested objects
    config.fieldConfig = fieldConfig;
    step.fieldConfig = fieldConfig;
    return <TStep><unknown>step;
  }

  private createStepInstance = <T>(
    id: string, name: string | undefined, path: string, nextStepDecider: IDecideNextStep | undefined,
    destinationConstructor: Constructor<T>
  ): T => new destinationConstructor(id, name, path, nextStepDecider);
}

